/**
 * La memoria della modale dell'esito (v. `esiti.ts`): localStorage per `sub`, con letture e scritture in
 * try/catch. Senza storage (o se lancia) resta la copia di SESSIONE di questo modulo: «una volta per
 * sessione», e una base che nasce adesso ⇒ niente di vecchio.
 * ⚠️ NON pura: tocca `window.localStorage`. La logica sta in `esiti.ts`.
 */
import { chiaveMemoriaEsiti, fondiViste, memoriaDa, memoriaNuova, type MemoriaEsiti } from './esiti.ts';


const memorieDiSessione = new Map<string, MemoriaEsiti>();

/** Solo per i banchi: dimentica le memorie di sessione. */
export function azzeraMemorieEsitiDiSessione(): void {
  memorieDiSessione.clear();
}

export function caricaMemoria(sub: string, ora: number): MemoriaEsiti {
  const inSessione = memorieDiSessione.get(sub);
  if (inSessione) return inSessione;
  let memoria: MemoriaEsiti | null = null;
  try {
    const grezza = window.localStorage.getItem(chiaveMemoriaEsiti(sub));
    memoria = grezza === null ? null : memoriaDa(JSON.parse(grezza), ora);
  } catch {
    memoria = null;
  }
  const m = memoria ?? memoriaNuova(ora);
  memorieDiSessione.set(sub, m);
  return m;
}

/**
 * Salva, e rende la memoria salvata. ⚠️ Le «viste» si FONDONO con quelle gia' nello storage (revisione di
 * #503, 🟡3): un'altra scheda puo' averne aggiunte, e sovrascriverle con le nostre le cancellerebbe prima
 * che qualcuno le rilegga.
 */
export function salvaMemoria(sub: string, memoria: MemoriaEsiti): MemoriaEsiti {
  let fusa = memoria;
  try {
    const grezza = window.localStorage.getItem(chiaveMemoriaEsiti(sub));
    const ora = Date.now();
    const altrove = grezza === null ? [] : memoriaDa(JSON.parse(grezza), ora).viste;
    fusa = { ...memoria, viste: fondiViste(memoria.viste, altrove, ora) };
  } catch {
    // Storage illeggibile: vale la nostra.
  }
  memorieDiSessione.set(sub, fusa);
  try {
    window.localStorage.setItem(chiaveMemoriaEsiti(sub), JSON.stringify(fusa));
  } catch {
    // Senza storage vale la copia di sessione.
  }
  return fusa;
}

/**
 * Lo storage dice che questo esito e' gia' stato mostrato ALTROVE (un'altra scheda, un'altra istanza della
 * mini app sullo stesso storage)? Si rilegge prima di mostrare (revisione di #503, 🟡3): la copia di
 * sessione non sa niente di chi scrive accanto a lei. ⚠️ Il limite: WebView di Telegram e browser hanno
 * storage separati, e fra loro una modale puo' uscire due volte. Senza storage risponde `false`.
 */
export function vistaNelloStorage(sub: string, chiave: string): boolean {
  try {
    const grezza = window.localStorage.getItem(chiaveMemoriaEsiti(sub));
    if (grezza === null) return false;
    return memoriaDa(JSON.parse(grezza), Date.now()).viste.some((v) => v.chiave === chiave);
  } catch {
    return false;
  }
}
